/**
 * THE MINE — drop it behind you, and whoever runs into it comes apart.
 *
 * The defensive half of the owner's list: "a bomb/mine to drop behind you as another circle is
 * chasing you and if they run into it, it splits their circle into several smaller". It is the
 * only thing in the game that a player *behind* you has to think about, which is what makes it
 * the answer to being chased rather than a second way to attack.
 *
 * It shatters over the same machinery the rocket and the spike do — see `rocket.ts` for why
 * there is no second shatter, and why it is the cell it touched rather than the whole cluster.
 *
 * **IT GOES OFF UNDER ITS OWNER TOO**, once armed, and that is a rule rather than an oversight:
 * a mine you cannot walk back onto is a mine with no downside, and the arming delay below is
 * exactly the window that separates "dropped it behind me while running" from "reversed into my
 * own trap". The card asks for it in those words and this is where it lives (`hitsOwner`).
 *
 * **IT IS VISIBLE TO EVERYONE.** Replication does not know who dropped what — the room's
 * projectiles go out whole to every viewer, exactly as the orbs and the spikes do — and that is
 * the choice, not an accident of the transport. A hidden mine is a far stronger item: it turns
 * a chase into a coin flip the chaser had no way to read, it cannot be played around, and the
 * only counter to it is to stop chasing anybody at all. A visible one is a *fence*: it says "not
 * this way", the chaser has to go round, and going round is the time the person who dropped it
 * bought. That is the mechanic the card describes, and it is the one that survives a room where
 * everybody has one.
 */

import { TICK_RATE_HZ } from '../constants/simulation.js';
import type { PowerUp } from './powerup.js';

export const POWER_UP_MINE = 'mine';

/**
 * Ticks before it is live — one second.
 *
 * "So you cannot mine your own face": the drop is placed a body's length behind a moving player,
 * and a player who is turning can be back on top of it inside a few ticks. A second is long
 * enough that dropping one while running away is always safe, and short enough that it is armed
 * before a chaser who was already on your tail arrives.
 */
export const MINE_ARM_TICKS = TICK_RATE_HZ;

/**
 * How long it lies there, in ticks — thirty seconds.
 *
 * The same reasoning POWER_UP_LIFETIME_SECONDS is set by, one step shorter: a mine that never
 * expired would make the map slowly fill up with hazards nobody remembers dropping, and the
 * room's own spikes are the permanent hazard. Thirty seconds is long enough to be a fence and
 * short enough that the arena a minute from now is the arena you can see.
 */
export const MINE_LIFETIME_TICKS = 30 * TICK_RATE_HZ;

/** Bigger than a rocket: it is a thing you have to see in time to steer around. */
export const MINE_RADIUS = 26;

/**
 * How far behind the dropping cell's rim it is laid, in world units.
 *
 * Clear of the body so it is not already touching the player who dropped it, and close enough
 * that it lands where a chaser is actually about to be rather than somewhere behind them.
 */
export const MINE_DROP_DISTANCE = 40;

/**
 * Firings one orb is worth. **Three.** The report named no model for the mine, so this is chosen
 * here and it is the one entry where the two models are not a matter of taste:
 *
 * **A mine is the only thing in the registry that ACCUMULATES.** It lies armed for
 * MINE_LIFETIME_TICKS — thirty seconds — so a timed window would not be "fire as much as you can
 * for ten seconds", it would be a minefield that outlives the window by half a minute and that
 * the player who laid it has to remember the shape of (`hitsOwner` is true, and only here).
 * Charged is therefore the only honest model, and the count is a **fence budget**: three is a
 * corner or a corridor, which is what a mine is for, and it is deliberately below the frost's
 * five because a mine shatters a cluster where a patch only slows one.
 */
export const MINE_CHARGES = 3;

/**
 * The gap between two mines, in ticks — **MINE_ARM_TICKS, one second.**
 *
 * The frost patch's rule, and for its reason: a mine is scenery until it arms and a wall
 * afterwards, so the next may be dropped on the tick the last one becomes a wall. Dropping all
 * three inside three seconds is a fence laid across a chase, which is the play; dropping three on
 * one tick would be a single lethal pixel, which is not.
 */
export const MINE_FIRE_COOLDOWN_TICKS = MINE_ARM_TICKS;

export const minePowerUp: PowerUp = {
  id: POWER_UP_MINE,
  name: 'Mine',
  description: 'Drops behind you, arms after a second, and shatters whoever runs into it.',
  glyph: '💣',
  use: {
    model: 'charged',
    charges: MINE_CHARGES,
    cooldownTicks: MINE_FIRE_COOLDOWN_TICKS,
  },
  projectile: {
    motion: 'dropped',
    effect: 'shatter',
    speed: 0,
    range: 0,
    lifetimeTicks: MINE_LIFETIME_TICKS,
    armingTicks: MINE_ARM_TICKS,
    radius: MINE_RADIUS,
    standoff: MINE_DROP_DISTANCE,
    // The deliberate one. See the note at the top of this file.
    hitsOwner: true,
  },
};
